/**
 * Open SQL File / Open Execution Plan / New Notebook / Open Notebook.
 *
 * Moved verbatim out of `MainToolbar.tsx` by task B11. Two guards that used to
 * live in the JSX `onClick` are folded into the actions so the descriptor's
 * `run` is the whole click path: Open SQL File's `fallbackConnectionId` check
 * (INJ-2's "selected, else first connected" resolution — do NOT narrow it back
 * to `selectedConnectionId`) and Open Execution Plan's browser-mode block.
 */
import { addBreadcrumb } from '../../crash-dialog/services/breadcrumbService';
import { toast } from '../../../components/ui/toaster';
import { importExecutionPlan } from '../../workspace/execution-plan/services/executionPlanService';
import { openDeadlockAnalyzerTab } from '../../workspace/deadlock-analyzer/openDeadlockAnalyzerTab';
import { openSqlFile } from '../../workspace/query-editor/services/queryFilesApi';
import { hashText } from '../../workspace/query-editor/utils/queryFileUtils';
import { openNotebookFile } from '../../workspace/notebook/services/notebookFileApi';
import { deserializeFromIpynb } from '../../workspace/notebook/utils/ipynbDeserializer';
import { computeNotebookHash } from '../../workspace/notebook/utils/ipynbSerializer';
import { useScriptBrowserStore } from '../../script-browser/store/scriptBrowserStore';
import { getDatabaseFromNodeId } from '../../explorer/utils/nodeIdUtils';
import { supportsNotebooksEngineFilter } from '../model/toolbarEngineFilters';
import type { Connection } from '../../../types/electron';
import type { RestrictedFeature } from '../../browser-mode';
import type { ToolbarActionContext } from './toolbarActionContext';
import { safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';

function getFileNameFromPath(filePath: string): string {
  const parts = filePath.split(/[/\\]/);
  return parts[parts.length - 1] || filePath;
}

type FileActionDeps = {
  fallbackConnectionId: string | undefined;
  selectedDatabase: string | null | undefined;
  connections: Connection[];
  checkAndBlock: (feature: RestrictedFeature) => boolean;
};

export function useToolbarFileActions(ctx: ToolbarActionContext, deps: FileActionDeps) {
  const { track, requestContext, addTab, getNextNotebookNumber, validDbSelection, activeConnections } = ctx;
  const { fallbackConnectionId, selectedDatabase, connections, checkAndBlock } = deps;

  const handleOpenExecutionPlan = async () => {
    addBreadcrumb('click', 'toolbar:execution-plan');
    track('toolbar:execution_plan');
    const result = await importExecutionPlan({});
    if (result.success) {
      addTab({
        title: `Plan - ${getFileNameFromPath(result.filePath)}`,
        type: 'plan',
        content: { view: 'single', plan: result.artifact, filePath: result.filePath },
      });
      return;
    }

    if (!result.cancelled) {
      toast.error({ title: 'Failed to open plan', description: result.error });
    }
  };

  const handleOpenQueryFile = async () => {
    addBreadcrumb('click', 'toolbar:open-query-file');
    track('toolbar:open_query_file');
    if (!fallbackConnectionId) return;

    const result = await openSqlFile({});
    if (result.success) {
      const activeConnection = activeConnections.find((conn) => conn.id === fallbackConnectionId);
      const connection = connections.find((conn) => conn.id === fallbackConnectionId);

      const databaseName = selectedDatabase
        ? (getDatabaseFromNodeId(selectedDatabase) ?? selectedDatabase)
        : activeConnection?.connection.database;

      const connectionInfo = activeConnection && connection
        ? {
          connectionId: fallbackConnectionId,
          connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
          serverHost: safeConnectionServer(activeConnection.connection),
          database: databaseName,
        }
        : undefined;

      const openedQuery = result.query ?? '';
      addTab({
        title: getFileNameFromPath(result.filePath),
        type: 'query',
        connectionInfo,
        content: {
          query: openedQuery,
          filePath: result.filePath,
          fileLastSavedHash: hashText(openedQuery),
        },
      });
      useScriptBrowserStore.getState().recordScriptOpened(result.filePath, openedQuery.slice(0, 100));
      toast.success({ title: 'Opened', description: result.filePath });
      return;
    }

    if (!result.cancelled) {
      toast.error({ title: 'Failed to open file', description: result.error });
    }
  };

  const handleNewNotebook = async () => {
    addBreadcrumb('click', 'toolbar:new-notebook');
    track('toolbar:new_notebook');
    const notebookNumber = getNextNotebookNumber();

    // Try to resolve context (prompts user if needed)
    const context = await requestContext(
      { requireConnection: true, requireDatabase: false, engineFilter: supportsNotebooksEngineFilter },
      'New Notebook'
    );

    // If user cancelled, create notebook without connection
    if (!context) {
      return addTab({ title: `Notebook-${notebookNumber}`, type: 'notebook' });
    }

    const databaseForTab =
      validDbSelection?.connectionId === context.connectionId
        ? validDbSelection.database
        : undefined;

    const connectionInfo = {
      connectionId: context.connectionId,
      connectionName: context.connectionName,
      serverHost: context.serverHost,
      database: databaseForTab,
    };

    return addTab({ title: `Notebook-${notebookNumber}`, type: 'notebook', connectionInfo });
  };

  const handleOpenNotebook = async () => {
    addBreadcrumb('click', 'toolbar:open-notebook');
    track('toolbar:open_notebook');

    const result = await openNotebookFile({});
    if (!result.success) {
      if ('cancelled' in result && result.cancelled) return;
      toast.error({ title: 'Failed to open notebook', description: result.error });
      return;
    }

    try {
      const { content, warnings } = deserializeFromIpynb(result.content);
      const fileName = getFileNameFromPath(result.filePath);
      const hash = computeNotebookHash(content);

      addTab({
        title: fileName,
        type: 'notebook',
        content: {
          ...content,
          filePath: result.filePath,
          lastSavedHash: hash,
        },
      });

      // Track in script browser
      const codeCells = content.cells.filter((c) => c.cellType === 'code').length;
      const mdCells = content.cells.filter((c) => c.cellType === 'markdown').length;
      const preview = `${codeCells} code cell${codeCells !== 1 ? 's' : ''}, ${mdCells} markdown cell${mdCells !== 1 ? 's' : ''}`;
      useScriptBrowserStore.getState().recordScriptOpened(result.filePath, preview);

      if (warnings.length > 0) {
        toast.warning({
          title: 'Notebook imported with warnings',
          description: warnings.join('; '),
        });
      } else {
        toast.success({ title: 'Opened', description: result.filePath });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to parse notebook';
      toast.error({ title: 'Invalid .ipynb file', description: message });
    }
  };

  return {
    'open-sql-file': () => {
      if (!fallbackConnectionId) return;
      void handleOpenQueryFile();
    },
    'open-execution-plan': () => {
      if (checkAndBlock('fileOpen')) return;
      void handleOpenExecutionPlan();
    },
    'deadlock-analyzer': () => {
      if (checkAndBlock('fileOpen')) return;
      addBreadcrumb('click', 'toolbar:deadlock-analyzer');
      track('toolbar:deadlock_analyzer');
      openDeadlockAnalyzerTab({ entry: 'toolbar' });
    },
    'new-notebook': () => void handleNewNotebook(),
    'open-notebook': () => void handleOpenNotebook(),
  };
}
